import type { ChangeEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Chapter } from "@/mocks/chapters";

interface ChapterNavProps {
  chapter: Chapter;
  list: Chapter[];
}

export default function ChapterNav({ chapter, list }: ChapterNavProps) {
  const navigate = useNavigate();
  const currentIndex = list.findIndex((c) => c.id === chapter.id);
  const prev = currentIndex > 0 ? list[currentIndex - 1] : null;
  const next = currentIndex < list.length - 1 ? list[currentIndex + 1] : null;

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    navigate(`/article/${e.target.value}`);
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-3">
      <div className="flex flex-col items-stretch gap-2 sm:flex-row">
        {prev ? (
          <LinkToChapter to={`/article/${prev.id}`} label="上一章" icon="ri-arrow-left-s-line" />
        ) : (
          <span className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-400">
            <i className="ri-arrow-left-s-line" aria-hidden="true" />
            已是第一话
          </span>
        )}

        <div className="relative flex-1">
          <select
            value={chapter.id}
            onChange={handleSelect}
            aria-label="选择章节"
            className="w-full appearance-none rounded-md border border-background-200 bg-background-100 px-4 py-2.5 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
          >
            {list.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <i
            className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
            aria-hidden="true"
          />
        </div>

        {next ? (
          <LinkToChapter to={`/article/${next.id}`} label="下一章" icon="ri-arrow-right-s-line" primary />
        ) : (
          <span className="flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-background-100 px-4 py-2.5 text-sm text-foreground-400">
            已是最新话
            <i className="ri-arrow-right-s-line" aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
}

function LinkToChapter({
  to,
  label,
  icon,
  primary = false,
}: {
  to: string;
  label: string;
  icon: string;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-center gap-1 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
        primary ? "bg-primary-500 text-background-50 hover:bg-primary-600" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
      }`}
    >
      <i className={icon} aria-hidden="true" />
      {label}
    </Link>
  );
}